import { db, storage } from '../firebaseConfig';
import { collection, addDoc, getDocs, doc, getDoc, query, where, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL, listAll } from 'firebase/storage';

// Tipos para las plantillas de miniaturas
export interface ThumbnailTemplate {
  id?: string;
  name: string;
  layers: any[];
  backgroundColor: string;
  createdAt?: Date;
  updatedAt?: Date;
  userId?: string;
}

// Tipo para las imágenes exportadas
export interface ExportedImage {
  id?: string;
  name: string;
  imageUrl: string;
  createdAt?: Date;
  userId: string;
}

const TEMPLATES_COLLECTION = 'thumbnailTemplates';
const EXPORTED_IMAGES_COLLECTION = 'exportedImages';

// Guardar una nueva plantilla
export const saveThumbnailTemplate = async (template: ThumbnailTemplate): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, TEMPLATES_COLLECTION), {
      ...template,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error al guardar la plantilla:', error);
    throw error;
  }
};

// Actualizar una plantilla existente
export const updateThumbnailTemplate = async (id: string, template: Partial<ThumbnailTemplate>): Promise<void> => {
  try {
    const docRef = doc(db, TEMPLATES_COLLECTION, id);
    await updateDoc(docRef, {
      ...template,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error al actualizar la plantilla:', error);
    throw error;
  }
};

// Obtener todas las plantillas
export const getAllThumbnailTemplates = async (userId?: string): Promise<ThumbnailTemplate[]> => {
  try {
    let q;
    if (userId) {
      q = query(collection(db, TEMPLATES_COLLECTION), where("userId", "==", userId));
    } else {
      q = collection(db, TEMPLATES_COLLECTION);
    }
    
    const querySnapshot = await getDocs(q);
    const templates: ThumbnailTemplate[] = [];
    
    querySnapshot.forEach((doc) => {
      templates.push({
        id: doc.id,
        ...doc.data() as ThumbnailTemplate
      });
    });
    
    return templates;
  } catch (error) {
    console.error('Error al obtener las plantillas:', error);
    throw error;
  }
};

// Obtener una plantilla por su ID
export const getThumbnailTemplateById = async (id: string): Promise<ThumbnailTemplate | null> => {
  try {
    const docRef = doc(db, TEMPLATES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data() as ThumbnailTemplate
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error al obtener la plantilla:', error);
    throw error;
  }
};

// Eliminar una plantilla
export const deleteThumbnailTemplate = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, TEMPLATES_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error al eliminar la plantilla:', error);
    throw error;
  }
};

// Subir una imagen a Firebase Storage y obtener su URL
export const uploadImageToStorage = async (imageDataUrl: string, userId: string): Promise<string> => {
  try {
    // Generar un nombre único para la imagen
    const timestamp = new Date().getTime();
    const randomString = Math.random().toString(36).substring(2, 8);
    const imageName = `thumbnails/${userId}/${timestamp}-${randomString}.jpg`;
    
    // Eliminar el prefijo 'data:image/...' para subir solo los datos binarios
    const imageData = imageDataUrl.split(',')[1];
    
    // Crear referencia en Storage
    const storageRef = ref(storage, imageName);
    
    // Subir la imagen
    await uploadString(storageRef, imageData, 'base64');
    
    // Obtener la URL de descarga
    const downloadUrl = await getDownloadURL(storageRef);
    
    return downloadUrl;
  } catch (error) {
    console.error('Error al subir la imagen:', error);
    throw error;
  }
};

// Guardar una imagen exportada en Firestore y Storage
export const saveExportedImage = async (imageDataUrl: string, userId: string, name?: string): Promise<ExportedImage> => {
  try {
    // Subir la imagen a Storage
    const imageUrl = await uploadImageToStorage(imageDataUrl, userId);
    
    // Generar un nombre por defecto si no se proporciona uno
    const imageName = name || `Miniatura ${new Date().toLocaleDateString()}`;
    
    // Crear el documento en Firestore
    const exportedImage: ExportedImage = {
      name: imageName,
      imageUrl,
      userId,
      createdAt: new Date()
    };
    
    const docRef = await addDoc(collection(db, EXPORTED_IMAGES_COLLECTION), exportedImage);
    
    return {
      ...exportedImage,
      id: docRef.id
    };
  } catch (error) {
    console.error('Error al guardar la imagen exportada:', error);
    throw error;
  }
};

// Obtener todas las imágenes exportadas de un usuario
export const getExportedImages = async (userId: string): Promise<ExportedImage[]> => {
  try {
    const q = query(collection(db, EXPORTED_IMAGES_COLLECTION), where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    const images: ExportedImage[] = [];
    
    querySnapshot.forEach((doc) => {
      images.push({
        id: doc.id,
        ...doc.data() as ExportedImage
      });
    });
    
    // Ordenar por fecha de creación (más recientes primero)
    return images.sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date((a.createdAt as any).seconds * 1000);
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date((b.createdAt as any).seconds * 1000);
      return dateB.getTime() - dateA.getTime();
    });
  } catch (error) {
    console.error('Error al obtener las imágenes exportadas:', error);
    throw error;
  }
};

// Eliminar una imagen exportada
export const deleteExportedImage = async (id: string): Promise<void> => {
  try {
    // Primero obtener la referencia para conseguir la URL de la imagen
    const docRef = doc(db, EXPORTED_IMAGES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const imageData = docSnap.data() as ExportedImage;
      
      // Eliminar el documento de Firestore
      await deleteDoc(docRef);
      
      // Nota: No eliminamos la imagen de Storage porque podría estar siendo utilizada por otras entidades
      // Si quisieras eliminarla también, necesitarías extraer la ruta del storage de la URL y eliminarla
    } else {
      throw new Error('La imagen no existe');
    }
  } catch (error) {
    console.error('Error al eliminar la imagen exportada:', error);
    throw error;
  }
};

// Procesar todas las imágenes de una plantilla antes de guardarla
export const processTemplateImages = async (template: ThumbnailTemplate): Promise<ThumbnailTemplate> => {
  if (!template.userId) {
    throw new Error('Se requiere un ID de usuario para procesar imágenes');
  }
  
  // Clonar la plantilla para no modificar la original
  const processedTemplate = { ...template };
  const processedLayers = [...template.layers];
  
  // Procesar cada capa
  for (let i = 0; i < processedLayers.length; i++) {
    const layer = processedLayers[i];
    
    // Si es una capa de imagen y tiene una URL de datos (no una URL remota)
    if (layer.type === 'image' && layer.imageUrl && layer.imageUrl.startsWith('data:')) {
      try {
        // Subir la imagen y obtener URL remota
        const remoteUrl = await uploadImageToStorage(layer.imageUrl, template.userId);
        
        // Actualizar la URL en la capa
        processedLayers[i] = {
          ...layer,
          imageUrl: remoteUrl
        };
      } catch (error) {
        console.error(`Error al procesar imagen en capa ${layer.id}:`, error);
        // Continuar con las demás capas aunque haya un error
      }
    }
  }
  
  // Actualizar las capas en la plantilla procesada
  processedTemplate.layers = processedLayers;
  
  return processedTemplate;
};
